import { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Grid,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { CiSearch } from "react-icons/ci";
import { GoHistory } from "react-icons/go";
import { PiEngineFill } from "react-icons/pi";
import { useQuery } from "@tanstack/react-query";
import { FaBriefcase, FaFileAlt, FaMoneyBillWave } from "react-icons/fa";
import { CreditEngineReportResponseData } from "@/models/consignado/report-data.model";
import { CreditAuctionProposal } from "@/models/credit-auction-proposal.model";
import { getCreditAuctionProposalComplete, getCreditEngineAuctionProposalReport } from "@/services/creditAuctionProposal.service";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";
import { documentFormatter } from "@/utils/documentFormatter";
import { DetailRow } from "./detail-row";
import { SectionCard } from "./section-card";
import { ProposalClientCard } from "./proposal-client-card";
import StatusFlow from "./proposal-client-card/status-flow";
import { formatDate, formatDateTime } from "./utils";
import { CreditAuctionProposalLogs } from "./credit-auction-proposal-logs";
import { CreditEngineReportProposal } from "@/components/consignado/ConsignadoProposalsPage/CreditEngineReportProposal";
import { getOfertaEscolhida } from "@/utils/consignado/ofertaEscolhida";

const NAO_INFORMADO = "Não informado";

const moneyOrMissing = (value?: number | null): string =>
  value != null ? BrlCurrencyFormatter(value) : NAO_INFORMADO;

const rateOrMissing = (value?: number | null): string =>
  value ? `${value.toFixed(2)}%` : NAO_INFORMADO;

interface CreditAuctionProposalDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  proposalId: string | null;
  proposal: CreditAuctionProposal | null;
}

function mergeProposalWithEngineReport(
  base: CreditAuctionProposal | null | undefined,
  report: CreditEngineReportResponseData | null
): CreditAuctionProposal | null {
  if (!base) return base ?? null;
  const fb = report?.fallback;
  if (!fb) return base;

  const isMissing = (value: string) => !value || value === "Não informado";

  return {
    ...base,
    NomeTrabalhador: isMissing(base.NomeTrabalhador) && fb.nome ? fb.nome : base.NomeTrabalhador,
    Cpf: isMissing(base.Cpf) && fb.cpf ? fb.cpf : base.Cpf,
    Email: isMissing(base.Email) && fb.email ? fb.email : base.Email,
    Telefone: isMissing(base.Telefone) && fb.telefone ? fb.telefone : base.Telefone,
    Idade: base.Idade ?? fb.idade ?? null,
    Sexo: isMissing(base.Sexo) && fb.sexo ? fb.sexo : base.Sexo,
    DataNascimento: isMissing(base.DataNascimento) && fb.dataNascimento ? fb.dataNascimento : base.DataNascimento,
    DataAdmissao: isMissing(base.DataAdmissao) && fb.dataAdmissao ? fb.dataAdmissao : base.DataAdmissao,
    InscricaoEmpregador:
      isMissing(base.InscricaoEmpregador.Descricao) && fb.empregadorNome
        ? { ...base.InscricaoEmpregador, Descricao: fb.empregadorNome }
        : base.InscricaoEmpregador,
    NumeroInscricaoEmpregador:
      isMissing(base.NumeroInscricaoEmpregador) && fb.empregadorCnpj
        ? fb.empregadorCnpj
        : base.NumeroInscricaoEmpregador,
    MargemReal:
      base.MargemReal > 0 ? base.MargemReal : fb.margemReal ?? base.MargemReal,
    MargemDisponivel:
      base.MargemDisponivel > 0 ? base.MargemDisponivel : fb.margemDisponivel ?? base.MargemDisponivel,
    TotalVencimentos:
      base.TotalVencimentos > 0 ? base.TotalVencimentos : fb.totalVencimentos ?? base.TotalVencimentos,
  };
}

export default function CreditAuctionProposalDetailModal({
  isOpen,
  onClose,
  proposalId,
  proposal,
}: CreditAuctionProposalDetailModalProps) {
  const { data: completeProposal, isLoading } = useQuery<CreditAuctionProposal>({
    queryKey: ["credit-auction-proposal-complete", proposalId],
    queryFn: () => getCreditAuctionProposalComplete(proposalId as string),
    enabled: isOpen && Boolean(proposalId),
  });

  const currentProposal = completeProposal ?? proposal;
  const [activeTab, setActiveTab] = useState<"principal" | "historico" | "analise-credito">("principal");
  
  const [hasEngineReport, setHasEngineReport] = useState(false);
  const [engineReportData, setEngineReportData] = useState<CreditEngineReportResponseData | null>(null);

  useEffect(() => {
    if (isOpen && proposalId) {
      getCreditEngineAuctionProposalReport(proposalId)
        .then(data => {
          setHasEngineReport(Boolean(data?.relatorios?.length));
          setEngineReportData(data ?? null);
        })
        .catch(() => {
          setHasEngineReport(false);
          setEngineReportData(null);
        });
    } else {
      setHasEngineReport(false);
      setEngineReportData(null);
      setActiveTab("principal");
    }
  }, [isOpen, proposalId]);

  const displayedProposal = mergeProposalWithEngineReport(currentProposal, engineReportData);
  // Oferta e seguro escolhidos no aceite do tomador, lidos do custom_status.
  // Proposta antes do aceite não tem o bloco: os campos ficam "Não informado".
  const { oferta, seguro, valorSeguro } = getOfertaEscolhida(displayedProposal?.CustomStatus);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={activeTab === "historico" || activeTab === "analise-credito" ? "2xl" : "3xl"} isCentered>
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(0.5rem)" />
      <ModalContent
        fontFamily="Lexend Deca"
        borderRadius={{ base: "20px", md: "20px" }}
        overflow="hidden"
        maxW={{ base: "90%", md: "90%", lg: "800px" }}
      >
        <ModalCloseButton color="white" zIndex={10} />
        
        {activeTab !== "principal" && (
          <Box
            bg="#58c46c"
            pt={2}
            pl={{ base: 3, md: 5 }}
            pb={2}
            borderTopRadius={{ base: "0", md: "20px" }}
          >
            <Text
              color="white"
              fontSize={{ base: "md", md: "sm" }}
              fontFamily="Lexend Deca"
            >
              Proposta de Leilão
            </Text>
            <Text
              color="white"
              fontSize={{ base: "md", md: "lg" }}
              fontFamily="Lexend Deca"
            >
              {displayedProposal?.NomeTrabalhador || "..."}
            </Text>
          </Box>
        )}

        <Box
          bg="secondary"
          p={{ base: 3, md: 3 }}
          borderTopRadius={{ base: "0", md: activeTab === "principal" ? "20px" : "0" }}
        >
          <Grid
            templateColumns={{ base: "repeat(2, 1fr)", md: hasEngineReport ? "repeat(3, 160px)" : "repeat(2, 160px)" }}
            gap={3}
            justifyContent="center"
          >
            <Flex
              align="center"
              justify="center"
              color="white"
              bg={activeTab === "principal" ? "rgba(255, 255, 255, 0.25)" : "rgba(255, 255, 255, 0.15)"}
              px={3}
              py={1.5}
              borderRadius="10px"
              h={{ base: "36px", md: "34px" }}
              boxShadow="sm"
              cursor="pointer"
              onClick={() => setActiveTab("principal")}
              _hover={{ bg: "rgba(255, 255, 255, 0.25)" }}
              transition="all 0.2s"
            >
              <Icon as={CiSearch} boxSize={5} mr={2} />
              <Text fontSize="sm" fontFamily="Lexend Deca">
                Visualizar
              </Text>
            </Flex>

            <Flex
              align="center"
              justify="center"
              color="white"
              bg={activeTab === "historico" ? "rgba(255, 255, 255, 0.25)" : "rgba(255, 255, 255, 0.15)"}
              px={3}
              py={1.5}
              borderRadius="10px"
              h={{ base: "36px", md: "34px" }}
              boxShadow="sm"
              cursor="pointer"
              onClick={() => setActiveTab("historico")}
              _hover={{ bg: "rgba(255, 255, 255, 0.25)" }}
              transition="all 0.2s"
            >
              <Icon as={GoHistory} boxSize={5} mr={2} />
              <Text fontSize="sm" fontFamily="Lexend Deca">
                Histórico
              </Text>
            </Flex>

            {hasEngineReport && (
              <Flex
                align="center"
                justify="center"
                color="white"
                bg={activeTab === "analise-credito" ? "rgba(255, 255, 255, 0.25)" : "rgba(255, 255, 255, 0.15)"}
                px={3}
                py={1.5}
                borderRadius="10px"
                h={{ base: "36px", md: "34px" }}
                boxShadow="sm"
                cursor="pointer"
                onClick={() => setActiveTab("analise-credito")}
                _hover={{ bg: "rgba(255, 255, 255, 0.25)" }}
                transition="all 0.2s"
              >
                <Icon as={PiEngineFill} boxSize={5} mr={2} />
                <Text fontSize="sm" fontFamily="Lexend Deca">
                  Análise crédito
                </Text>
              </Flex>
            )}
          </Grid>
        </Box>

        <ModalBody bg="#F8FAFC" p={{ base: 4, md: 5 }}>
          {isLoading ? (
            <Flex minH="280px" align="center" justify="center">
              <Spinner size="xl" color="gray.500" />
            </Flex>
          ) : !displayedProposal ? null : (
          <VStack align="stretch" spacing={4}>

            {activeTab === "principal" && (
              <VStack align="stretch" spacing={4}>
                <Flex
                  bg="white"
                  border="1px solid"
                  borderColor="#E2E8F0"
                  borderRadius="16px"
                  py={5}
                  px={6}
                  direction="column"
                  align="center"
                  gap={4}
                >
                  <Text
                    fontFamily="Lexend Deca"
                    fontSize="sm"
                    fontWeight="500"
                    color="secondary"
                    letterSpacing="0.04em"
                    textTransform="uppercase"
                  >
                    Progresso no leilão
                  </Text>
                  <StatusFlow currentStatus={displayedProposal.Status} />
                </Flex>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <ProposalClientCard payload={displayedProposal} />
              <SectionCard icon={FaBriefcase} title="Vínculo empregatício e margens">
                <DetailRow
                  label="Empregador"
                  value={displayedProposal.InscricaoEmpregador.Descricao}
                  valueFontSize="xs"
                />
                <DetailRow
                  label="CNPJ"
                  value={
                    displayedProposal.NumeroInscricaoEmpregador &&
                    displayedProposal.NumeroInscricaoEmpregador !== "Não informado"
                      ? documentFormatter(displayedProposal.NumeroInscricaoEmpregador)
                      : "Não informado"
                  }
                />
                <DetailRow
                  label="Admissão"
                  value={
                    displayedProposal.DataAdmissao
                      ? formatDate(displayedProposal.DataAdmissao)
                      : "Não informado"
                  }
                />
                <DetailRow
                  label="Matrícula"
                  value={displayedProposal.Matricula}
                />
                <DetailRow
                  label="Total Vencimentos"
                  value={
                    displayedProposal.TotalVencimentos > 0
                      ? BrlCurrencyFormatter(displayedProposal.TotalVencimentos)
                      : "Não informado"
                  }
                />
                <DetailRow
                  label="Margem Real"
                  value={
                    displayedProposal.MargemReal > 0
                      ? BrlCurrencyFormatter(displayedProposal.MargemReal)
                      : "Não informado"
                  }
                />
                <DetailRow
                  label="Margem Disponível"
                  value={
                    displayedProposal.MargemDisponivel > 0
                      ? BrlCurrencyFormatter(displayedProposal.MargemDisponivel)
                      : "Não informado"
                  }
                  badge={displayedProposal.MargemDisponivel > 0}
                />
              </SectionCard>
              </SimpleGrid>

            <SectionCard icon={FaFileAlt} title="Proposta de Leilão">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={8} spacingY={3}>
                <VStack align="stretch" spacing={3}>
                  <DetailRow
                    label="ID da Proposta"
                    value={displayedProposal.id}
                    copyable
                  />
                  <DetailRow
                    label="ID Solicitação"
                    value={String(displayedProposal.IdSolicitacao)}
                  />
                  <DetailRow
                    label="Short Hash"
                    value={displayedProposal.ShortHash}
                  />
                  <DetailRow
                    label="Data de Criação"
                    value={formatDateTime(displayedProposal.CreatedAt)}
                  />
                  <DetailRow
                    label="Data de Validade"
                    value={formatDateTime(displayedProposal.DataHoraValidadeSolicitacao)}
                    badge
                    badgeColorScheme="red"
                  />
                </VStack>
                <VStack align="stretch" spacing={3}>
                  <DetailRow
                    label="Parcelas"
                    value={`${displayedProposal.NroParcelas} meses`}
                  />
                  <DetailRow
                    label="Taxa Mensal"
                    value={`${displayedProposal.TaxaMensal * 100}%`}
                  />
                  <DetailRow
                    label="Data Pagamento"
                    value={
                      displayedProposal.DataDesembolso
                        ? formatDate(displayedProposal.DataDesembolso)
                        : "Não informado"
                    }
                  />
                  <DetailRow
                    label="URL Contratação"
                    value={
                      displayedProposal.UrlAutocontratacao
                        ? "Copiar link"
                        : "Não informado"
                    }
                    copyable={Boolean(displayedProposal.UrlAutocontratacao)}
                    copyValue={displayedProposal.UrlAutocontratacao ?? undefined}
                    valueTooltip={
                      displayedProposal.UrlAutocontratacao ?? undefined
                    }
                  />
                </VStack>
              </SimpleGrid>
            </SectionCard>

            <SectionCard icon={FaMoneyBillWave} title="Oferta escolhida">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={8} spacingY={3}>
                <VStack align="stretch" spacing={3}>
                  <DetailRow
                    label="Valor Liberado"
                    value={moneyOrMissing(oferta?.releasedAmount)}
                    badge={oferta?.releasedAmount != null}
                  />
                  <DetailRow
                    label="Parcelas"
                    value={
                      oferta?.numberOfInstallments
                        ? `${oferta.numberOfInstallments} x ${BrlCurrencyFormatter(oferta.installmentValue)}`
                        : NAO_INFORMADO
                    }
                  />
                  <DetailRow
                    label="Valor Descontado"
                    value={moneyOrMissing(oferta?.loanAmount)}
                  />
                  <DetailRow
                    label="IOF"
                    value={moneyOrMissing(oferta?.iofAmount)}
                  />
                  <DetailRow
                    label="Seguro"
                    value={
                      valorSeguro
                        ? BrlCurrencyFormatter(valorSeguro)
                        : oferta?.insuranceValue === 0
                        ? "Sem seguro"
                        : NAO_INFORMADO
                    }
                  />
                </VStack>
                <VStack align="stretch" spacing={3}>
                  <DetailRow
                    label="Taxa Mensal"
                    value={rateOrMissing(oferta?.monthlyRate)}
                  />
                  <DetailRow
                    label="Taxa Anual"
                    value={rateOrMissing(oferta?.annualRate)}
                  />
                  <DetailRow
                    label="CET Mensal"
                    value={rateOrMissing(oferta?.monthlyCETAmount)}
                  />
                  <DetailRow
                    label="CET Anual"
                    value={rateOrMissing(oferta?.annualCETAmount)}
                  />
                  <DetailRow
                    label="Apólice"
                    value={seguro?.policyId ?? NAO_INFORMADO}
                  />
                </VStack>
              </SimpleGrid>
            </SectionCard>
              </VStack>
            )}

            {activeTab === "historico" && (
              <VStack align="stretch" spacing={6}>
                {displayedProposal.CustomStatus ? (
                  <Box
                    bg="#FFF"
                    border="1px solid"
                    borderColor="#E2E8F0"
                    borderRadius="14px"
                    p={4}
                  >
                    <HStack spacing={2} mb={1}>
                      <Icon as={FaFileAlt} color="#8F9BA8" boxSize={4} />
                      <Text fontSize="md" fontWeight="800" color="#334155" fontFamily="Lexend Deca">
                        Logs de Processamento
                      </Text>
                    </HStack>
                    <CreditAuctionProposalLogs logsStr={displayedProposal.CustomStatus} />
                  </Box>
                ) : (
                  <Box py={8} textAlign="center">
                    <Text color="#64748B" fontFamily="Lexend Deca">Nenhum histórico disponível para esta proposta.</Text>
                  </Box>
                )}
              </VStack>
            )}
            
            {activeTab === "analise-credito" && hasEngineReport && (
              <VStack align="stretch" spacing={6}>
                <CreditEngineReportProposal 
                  proposalId={displayedProposal.id} 
                  fetchReportData={() => Promise.resolve(engineReportData)}
                />
              </VStack>
            )}

          </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
